import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import {
  trackPageView,
  trackSessionStart,
  trackCampaignLanding,
  setUserProperties,
  trackContentEngagement,
} from '@/lib/analytics';
import { useAuth } from './AuthContext';

interface AnalyticsContextType {
  trackThemeChange: (theme: 'light' | 'dark') => void;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};

interface AnalyticsProviderProps {
  children: ReactNode;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();

  // Track page views on route change
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  // Track session start and campaign landing on initial load
  useEffect(() => {
    trackSessionStart();
    trackCampaignLanding();
  }, []);

  // Update user properties when auth state changes
  useEffect(() => {
    setUserProperties({
      user_type: isAuthenticated ? 'client' : 'guest',
      user_id: user?.id,
      is_authenticated: isAuthenticated,
    });
  }, [user, isAuthenticated]);

  // Scroll depth tracking
  useEffect(() => {
    let maxScroll = 0;
    const thresholds = [25, 50, 75, 90];
    const trackedThresholds = new Set<number>();

    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollHeight <= 0) return;

      const scrollPercent = Math.round((window.scrollY / scrollHeight) * 100);

      if (scrollPercent > maxScroll) {
        maxScroll = scrollPercent;

        thresholds.forEach((threshold) => {
          if (scrollPercent >= threshold && !trackedThresholds.has(threshold)) {
            trackedThresholds.add(threshold);
            trackContentEngagement.scrollDepth(threshold, location.pathname);
          }
        });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname]);

  const trackThemeChange = (theme: 'light' | 'dark') => {
    trackContentEngagement.themeToggle(theme);
  };

  const value: AnalyticsContextType = {
    trackThemeChange,
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};
